import { useEffect } from "react";
import { useSelector } from "react-redux";
import {
  BrowserRouter,
  Routes as MainRoutes,
  Navigate,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Header from "./components/Header";
import Navbar from "./components/Navbar";
import Cart from "./pages/Cart";
import Home from "./pages/Home";
import LandingPage from "./pages/LandingPage";
import MobileNumberInput from "./pages/MobileNumberInput";
import Onboarding from "./pages/Onboarding";
import PaymentPage from "./pages/PaymentPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Failed from "./pages/PaymentPage/Failed";
import Status from "./pages/PaymentPage/Status";
import Success from "./pages/PaymentPage/Success";
import SplashScreen from "./pages/SplashScreen";
import TripGenerate from "./pages/TripGenerate";
import VerificationCodeInput from "./pages/VerificationCodeInput";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import About from "./pages/About";
import Features from "./pages/Features";
import Gallery from "./pages/Gallery";
import Indore from "./pages/Destinations/Indore";
import Ujjain from "./pages/Destinations/Ujjain";
import Destinations from "./pages/Destinations";
import Places from "./pages/Places";
import RajwadaPalace from "./pages/Destinations/Indore/RajwadaPalace";
import SarafaBazaar from "./pages/Destinations/Indore/SarafaBazaar";
import LalBaghPalace from "./pages/Destinations/Indore/LalBaghPalace";
import KanchMandir from "./pages/Destinations/Indore/KanchMandir";
import CentralMuseum from "./pages/Destinations/Indore/CentralMuseum";
import ChoralDam from "./pages/Destinations/Indore/ChoralDam";
import MahakaleshwarTemple from "./pages/Destinations/Ujjain/MahakaleshwarTemple";
import KalBhairavTemple from "./pages/Destinations/Ujjain/KalBhairavTemple";

// Additional Indore Pages
import KhajranaGaneshTemple from "./pages/Destinations/Indore/KhajranaGaneshTemple";
import AnnapurnaTemple from "./pages/Destinations/Indore/AnnapurnaTemple";
import BadaGanpatiTemple from "./pages/Destinations/Indore/BadaGanpatiTemple";
import BijasanMataTemple from "./pages/Destinations/Indore/BijasanMataTemple";
import RalamandalWildlifeSanctuary from "./pages/Destinations/Indore/RalamandalWildlifeSanctuary";
import PatalpaniWaterfall from "./pages/Destinations/Indore/PatalpaniWaterfall";
import TinchaFalls from "./pages/Destinations/Indore/TinchaFalls";

// New Indore Pages
import DevGuradiaTemple from "./pages/Destinations/Indore/DevGuradiaTemple";
import DewasTemple from "./pages/Destinations/Indore/DewasTemple";
import GopalTemple from "./pages/Destinations/Indore/GopalTemple";
import ISKCON from "./pages/Destinations/Indore/ISKCON";
import JanapavTemple from "./pages/Destinations/Indore/JanapavTemple";
import KankeshwariTemple from "./pages/Destinations/Indore/KankeshwariTemple";
import BamniyaWaterfall from "./pages/Destinations/Indore/BamniyaWaterfall";
import ChidiyaWaterfall from "./pages/Destinations/Indore/ChidiyaWaterfall";

const Routes = () => {
  const location = useLocation();
  const excludedPaths = [
    "/",
    "/landing",
    "/privacy-policy",
    "/onboarding",
    "/payment",
    "/payment-success",
    "/payment-failed",
    "/splash",
    "/privacy-policy",
    "/blog",
    "/about",
    "/features",
    "/gallery",
    "/destinations",
    "/places",
    "/destinations/indore",
    "/destinations/ujjain",
    "/destinations/indore/rajwada-palace",
    "/destinations/indore/sarafa-bazaar",
    "/destinations/indore/lal-bagh-palace",
    "/destinations/indore/kanch-mandir",
    "/destinations/indore/central-museum",
    "/destinations/indore/choral-dam",
    "/destinations/indore/khajrana-ganesh-temple",
    "/destinations/indore/annapurna-temple",
    "/destinations/indore/bada-ganpati-temple",
    "/destinations/indore/bijasan-mata-temple",
    "/destinations/indore/ralamandal-wildlife-sanctuary",
    "/destinations/indore/patalpani-waterfall",
    "/destinations/indore/tincha-falls",
    "/destinations/indore/dev-guradia-temple",
    "/destinations/indore/dewas-temple",
    "/destinations/indore/gopal-temple",
    "/destinations/indore/iskcon",
    "/destinations/indore/janapav-temple",
    "/destinations/indore/kankeshwari-temple",
    "/destinations/indore/bamniya-waterfall",
    "/destinations/indore/chidiya-waterfall",
    "/destinations/ujjain/mahakaleshwar-temple",
    "/destinations/ujjain/kal-bhairav-temple",
  ];
  const onboarding = useSelector((state) => state.onboarding);
  const navigate = useNavigate();

  // Check if current page should have full-width layout
  const isFullWidthPage = location.pathname === "/" || 
                          location.pathname === "/privacy-policy" || 
                          location.pathname.startsWith("/blog") ||
                          location.pathname === "/about" ||
                          location.pathname === "/features" ||
                          location.pathname === "/gallery" ||
                          location.pathname === "/destinations" ||
                          location.pathname === "/places" ||
                          location.pathname.startsWith("/destinations/");

  useEffect(() => {
    // Only redirect users to root if they're trying to access protected app pages without completing onboarding
    // Allow access to onboarding page even without onboarding data
    const protectedPaths = ["/home", "/cart", "/enter-mobile", "/verify-code", "/payment", "/payment-success", "/payment-failed", "/trip-generate", "/payment-status"];
    
    if (!onboarding.city && onboarding.step === 0 && 
        protectedPaths.includes(location.pathname)) {
      navigate("/");
    }
  }, [navigate, onboarding.city, onboarding.step, location.pathname]);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={true}
        newestOnTop={true}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
      
      {isFullWidthPage ? (
        // Full width layout with universal navbar
        <div className="min-h-screen">
          {location.pathname !== "/splash" && <Navbar />}
          <MainRoutes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/splash" element={<SplashScreen />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/home" element={<Home />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/enter-mobile" element={<MobileNumberInput />} />
            <Route path="/verify-code" element={<VerificationCodeInput />} />
            <Route path="/payment" element={<PaymentPage />} />
            <Route path="/payment-success" element={<Success />} />
            <Route path="/payment-failed" element={<Failed />} />
            <Route path="/trip-generate" element={<TripGenerate />} />
            <Route path="/payment-status" element={<Status />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/features" element={<Features />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/destinations" element={<Destinations />} />
            <Route path="/places" element={<Places />} />
            <Route path="/destinations/indore" element={<Indore />} />
            <Route path="/destinations/ujjain" element={<Ujjain />} />
            <Route path="/destinations/indore/rajwada-palace" element={<RajwadaPalace />} />
            <Route path="/destinations/indore/sarafa-bazaar" element={<SarafaBazaar />} />
            <Route path="/destinations/indore/lal-bagh-palace" element={<LalBaghPalace />} />
            <Route path="/destinations/indore/kanch-mandir" element={<KanchMandir />} />
            <Route path="/destinations/indore/central-museum" element={<CentralMuseum />} />
            <Route path="/destinations/indore/choral-dam" element={<ChoralDam />} />
            <Route path="/destinations/indore/khajrana-ganesh-temple" element={<KhajranaGaneshTemple />} />
            <Route path="/destinations/indore/annapurna-temple" element={<AnnapurnaTemple />} />
            <Route path="/destinations/indore/bada-ganpati-temple" element={<BadaGanpatiTemple />} />
            <Route path="/destinations/indore/bijasan-mata-temple" element={<BijasanMataTemple />} />
            <Route path="/destinations/indore/ralamandal-wildlife-sanctuary" element={<RalamandalWildlifeSanctuary />} />
            <Route path="/destinations/indore/patalpani-waterfall" element={<PatalpaniWaterfall />} />
            <Route path="/destinations/indore/tincha-falls" element={<TinchaFalls />} />
            <Route path="/destinations/indore/dev-guradia-temple" element={<DevGuradiaTemple />} />
            <Route path="/destinations/indore/dewas-temple" element={<DewasTemple />} />
            <Route path="/destinations/indore/gopal-temple" element={<GopalTemple />} />
            <Route path="/destinations/indore/iskcon" element={<ISKCON />} />
            <Route path="/destinations/indore/janapav-temple" element={<JanapavTemple />} />
            <Route path="/destinations/indore/kankeshwari-temple" element={<KankeshwariTemple />} />
            <Route path="/destinations/indore/bamniya-waterfall" element={<BamniyaWaterfall />} />
            <Route path="/destinations/indore/chidiya-waterfall" element={<ChidiyaWaterfall />} />
            <Route path="/destinations/ujjain/mahakaleshwar-temple" element={<MahakaleshwarTemple />} />
            <Route path="/destinations/ujjain/kal-bhairav-temple" element={<KalBhairavTemple />} />
            <Route path="*" element={<Navigate to="/home" replace />} />
          </MainRoutes>
        </div>
      ) : (
        // Mobile constrained layout for all other pages
        <div className="min-h-screen bg-gray-200">
          <div
            className="relative mx-auto bg-white min-h-screen flex flex-col"
            style={{ maxWidth: "430px" }}
          >
            {!excludedPaths.includes(location.pathname) && !location.pathname.startsWith("/blog/") && <Header />}
            <MainRoutes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/splash" element={<SplashScreen />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/home" element={<Home />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/enter-mobile" element={<MobileNumberInput />} />
              <Route path="/verify-code" element={<VerificationCodeInput />} />
              <Route path="/payment" element={<PaymentPage />} />
              <Route path="/payment-success" element={<Success />} />
              <Route path="/payment-failed" element={<Failed />} />
              <Route path="/trip-generate" element={<TripGenerate />} />
              <Route path="/payment-status" element={<Status />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/features" element={<Features />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/destinations" element={<Destinations />} />
              <Route path="/places" element={<Places />} />
              <Route path="/destinations/indore" element={<Indore />} />
              <Route path="/destinations/ujjain" element={<Ujjain />} />
              <Route path="/destinations/indore/rajwada-palace" element={<RajwadaPalace />} />
              <Route path="/destinations/indore/sarafa-bazaar" element={<SarafaBazaar />} />
              <Route path="/destinations/indore/lal-bagh-palace" element={<LalBaghPalace />} />
              <Route path="/destinations/indore/kanch-mandir" element={<KanchMandir />} />
              <Route path="/destinations/indore/central-museum" element={<CentralMuseum />} />
              <Route path="/destinations/indore/choral-dam" element={<ChoralDam />} />
              <Route path="/destinations/indore/khajrana-ganesh-temple" element={<KhajranaGaneshTemple />} />
              <Route path="/destinations/indore/annapurna-temple" element={<AnnapurnaTemple />} />
              <Route path="/destinations/indore/bada-ganpati-temple" element={<BadaGanpatiTemple />} />
              <Route path="/destinations/indore/bijasan-mata-temple" element={<BijasanMataTemple />} />
              <Route path="/destinations/indore/ralamandal-wildlife-sanctuary" element={<RalamandalWildlifeSanctuary />} />
              <Route path="/destinations/indore/patalpani-waterfall" element={<PatalpaniWaterfall />} />
              <Route path="/destinations/indore/tincha-falls" element={<TinchaFalls />} />
              <Route path="/destinations/indore/dev-guradia-temple" element={<DevGuradiaTemple />} />
              <Route path="/destinations/indore/dewas-temple" element={<DewasTemple />} />
              <Route path="/destinations/indore/gopal-temple" element={<GopalTemple />} />
              <Route path="/destinations/indore/iskcon" element={<ISKCON />} />
              <Route path="/destinations/indore/janapav-temple" element={<JanapavTemple />} />
              <Route path="/destinations/indore/kankeshwari-temple" element={<KankeshwariTemple />} />
              <Route path="/destinations/indore/bamniya-waterfall" element={<BamniyaWaterfall />} />
              <Route path="/destinations/indore/chidiya-waterfall" element={<ChidiyaWaterfall />} />
              <Route path="/destinations/ujjain/mahakaleshwar-temple" element={<MahakaleshwarTemple />} />
              <Route path="/destinations/ujjain/kal-bhairav-temple" element={<KalBhairavTemple />} />
              <Route path="*" element={<Navigate to="/home" replace />} />
            </MainRoutes>
          </div>
        </div>
      )}
    </>
  );
};

export default function RoutesWrapper() {
  return (
    <BrowserRouter>
      <Routes />
    </BrowserRouter>
  );
}
